// src/components/UI/Button.jsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const BaseStyledButton = styled(motion.button)`
  padding: ${({ size }) => 
    size === 'large' ? '1rem 2rem' : 
    size === 'small' ? '0.5rem 1rem' : 
    '0.75rem 1.5rem'
  };
  
  font-size: ${({ size }) => 
    size === 'large' ? '1.1rem' : 
    size === 'small' ? '0.875rem' : 
    '1rem'
  };
  
  font-weight: 600;
  font-family: ${({ theme }) => theme.fonts.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  border: none;
  border-radius: ${({ variant }) => variant === 'round' ? '50px' : '10px'};
  cursor: pointer;
  
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  
  position: relative;
  overflow: hidden;
  
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.smooth};
  
  /* Primary button styles */
  ${({ variant, theme }) => variant === 'primary' && `
    background: ${theme.gradients.primary};
    color: ${theme.colors.background.primary};
    box-shadow: ${theme.shadows.button};
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(255, 229, 0, 0.4);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
  
  /* Secondary button styles */
  ${({ variant, theme }) => variant === 'secondary' && `
    background: transparent;
    color: ${theme.colors.text.primary};
    border: 2px solid ${theme.colors.primary.blue};
    
    &:hover {
      background: ${theme.colors.primary.blue};
      color: ${theme.colors.text.primary};
      transform: translateY(-2px);
    }
  `}
  
  /* Cyber button styles */
  ${({ variant, theme }) => variant === 'cyber' && `
    background: ${theme.gradients.cyber};
    color: ${theme.colors.text.primary};
    box-shadow: ${theme.shadows.glow};
    border: 1px solid ${theme.colors.primary.purple};
    
    &:hover {
      transform: translateY(-2px) scale(1.02);
      box-shadow: 0 12px 40px rgba(155, 89, 182, 0.6);
    }
  `}
  
  /* Ghost button styles */
  ${({ variant, theme }) => variant === 'ghost' && `
    background: ${theme.colors.surface};
    color: ${theme.colors.text.secondary};
    border: 1px solid ${theme.colors.border};
    backdrop-filter: blur(10px);
    
    &:hover {
      background: ${theme.colors.primary.blue}20;
      color: ${theme.colors.text.primary};
      border-color: ${theme.colors.primary.blue};
    }
  `}
  
  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
  
  /* Shimmer effect */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const StyledLink = styled(motion.a)`
  padding: ${({ size }) => 
    size === 'large' ? '1rem 2rem' : 
    size === 'small' ? '0.5rem 1rem' : 
    '0.75rem 1.5rem'
  };
  
  font-size: ${({ size }) => 
    size === 'large' ? '1.1rem' : 
    size === 'small' ? '0.875rem' : 
    '1rem'
  };
  
  font-weight: 600;
  font-family: ${({ theme }) => theme.fonts.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-decoration: none;
  
  border: none;
  border-radius: ${({ variant }) => variant === 'round' ? '50px' : '10px'};
  cursor: pointer;
  
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  
  position: relative;
  overflow: hidden;
  
  transition: all ${({ theme }) => theme.animations.duration.normal} ${({ theme }) => theme.animations.easing.smooth};
  
  /* Primary button styles */
  ${({ variant, theme }) => variant === 'primary' && `
    background: ${theme.gradients.primary};
    color: ${theme.colors.background.primary};
    box-shadow: ${theme.shadows.button};
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(255, 229, 0, 0.4);
    }
    
    &:active {
      transform: translateY(0);
    }
  `}
  
  /* Secondary button styles */
  ${({ variant, theme }) => variant === 'secondary' && `
    background: transparent;
    color: ${theme.colors.text.primary};
    border: 2px solid ${theme.colors.primary.blue};
    
    &:hover {
      background: ${theme.colors.primary.blue};
      color: ${theme.colors.text.primary};
      transform: translateY(-2px);
    }
  `}
  
  /* Cyber button styles */
  ${({ variant, theme }) => variant === 'cyber' && `
    background: ${theme.gradients.cyber};
    color: ${theme.colors.text.primary};
    box-shadow: ${theme.shadows.glow};
    border: 1px solid ${theme.colors.primary.purple};
    
    &:hover {
      transform: translateY(-2px) scale(1.02);
      box-shadow: 0 12px 40px rgba(155, 89, 182, 0.6);
    }
  `}
  
  /* Ghost button styles */
  ${({ variant, theme }) => variant === 'ghost' && `
    background: ${theme.colors.surface};
    color: ${theme.colors.text.secondary};
    border: 1px solid ${theme.colors.border};
    backdrop-filter: blur(10px);
    
    &:hover {
      background: ${theme.colors.primary.blue}20;
      color: ${theme.colors.text.primary};
      border-color: ${theme.colors.primary.blue};
    }
  `}
  
  /* Shimmer effect */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }
  
  &:hover::before {
    left: 100%;
  }
`;

const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  onClick,
  disabled = false,
  className,
  href,
  target,
  rel,
  ...props
}) => {
  const buttonVariants = {
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.2,
        ease: "easeOut"
      }
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1,
        ease: "easeOut"
      }
    }
  };

  // If href is provided, render as a link
  if (href) {
    return (
      <StyledLink
        href={href}
        target={target || "_blank"}
        rel={rel || "noopener noreferrer"}
        variant={variant}
        size={size}
        className={className}
        variants={buttonVariants}
        whileHover="hover"
        whileTap="tap"
        {...props}
      >
        {children}
      </StyledLink>
    );
  }

  // Otherwise, render as a button
  return (
    <BaseStyledButton
      variant={variant}
      size={size}
      onClick={onClick}
      disabled={disabled}
      className={className}
      variants={buttonVariants}
      whileHover="hover"
      whileTap="tap"
      {...props}
    >
      {children}
    </BaseStyledButton>
  );
};

export default Button;